"use client";

import loadScript from "../../helpers/loadScript";
import classNames from "classnames/bind";
import { useEffect } from "react";
import styles from "./Header.module.css";

const cx = classNames.bind(styles);

interface MenuConfig {
  locale: string;
  theme: "dark" | "light";
  opaque: boolean;
  searchDeactivated: boolean;
}
/**
 * This Header component implements the instructions in the Red Bull Platforms
 * documentation for embedding the redbull.com navigation menu. You can read
 * more about that here:
 * @see {@link https://build.redbull.com/building-blocks/global-red-bull-nav-bar}
 */

export const Header = ({
  dir,
  localeString,
  className,
  theme,
  stickyDisabled,
}: {
  dir: "ltr" | "rtl";
  localeString: string;
  className?: string;
  theme?: MenuConfig["theme"];
  /** Passed to rbcom-navigation to disable sticky header (e.g. on support-hub pages). */
  stickyDisabled?: boolean;
}) => {
  // Previously `theme` and `opaque` were configurable in the CMS
  const menuConfig: MenuConfig = {
    locale: localeString,
    theme: theme || "light", // Fallback to light if no theme provided
    opaque: true,
    searchDeactivated: true,
  };

  useEffect(() => {
    loadScript(
      dir === "rtl"
        ? "https://www.redbull.com/v3/widgets/navigation/navigation.rtl.js"
        : "https://www.redbull.com/v3/widgets/navigation/navigation.js",
      { defer: true },
    );
  }, [dir]);

  return (
    <div className={cx("container", className)}>
      <rbcom-navigation
        locale={menuConfig.locale}
        theme={menuConfig.theme}
        {...(menuConfig.opaque && { opaque: true })}
        {...(menuConfig.searchDeactivated && { "search-deactivated": true })}
        {...(stickyDisabled && { "sticky-disabled": true })}
      ></rbcom-navigation>
    </div>
  );
};

/**
 * This is a bit of technical debt, honestly. If you need to forcefully hide the
 * menu, then you'd use this component anywhere on the page.
 */
export const HeaderHider = () => <div className={cx("hider")} />;
